import { call, put, takeEvery, takeLatest, all, fork } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    conversationService,
    ConversationSession,
    ConversationMessage,
    ConversationProgress,
    ConversationFeedback,
    ConversationAnalytics,
    LessonContent,
    CreateConversationRequest,
    SubmitMessageRequest,
    CreateAIConversationRequest,
    AIConversationFilters,
    AIConversationResponse,
    AIConversationSession,
    SessionCompletionData,
} from '@/services/conversation';

// Conversation Session Actions
import {
    createConversationSession,
    createConversationSessionSuccess,
    createConversationSessionFailure,
    fetchConversationSession,
    fetchConversationSessionSuccess,
    fetchConversationSessionFailure,
    updateConversationSession,
    updateConversationSessionSuccess,
    updateConversationSessionFailure,
    completeConversationSession,
    completeConversationSessionSuccess,
    completeConversationSessionFailure,
    fetchSessionLessons,
    fetchSessionLessonsSuccess,
    fetchSessionLessonsFailure,
    startLesson,
    startLessonSuccess,
    startLessonFailure,
    completeLesson,
    completeLessonSuccess,
    completeLessonFailure,
    fetchUserSessions,
    fetchUserSessionsSuccess,
    fetchUserSessionsFailure,
    setCurrentSession,
} from '@/store/slices/conversation/conversationSessionSlice';

// Conversation Message Actions
import {
    fetchSessionMessages,
    fetchSessionMessagesSuccess,
    fetchSessionMessagesFailure,
    sendMessage,
    sendMessageSuccess,
    sendMessageFailure,
    pinMessage,
    pinMessageSuccess,
    pinMessageFailure,
    fetchPinnedMessages,
    fetchPinnedMessagesSuccess,
    fetchPinnedMessagesFailure,
} from '@/store/slices/conversation/conversationMessageSlice';

// Conversation Progress Actions
import {
    fetchSessionProgress,
    fetchSessionProgressSuccess,
    fetchSessionProgressFailure,
    updateSessionProgress,
    updateSessionProgressSuccess,
    updateSessionProgressFailure,
    fetchSessionFeedback,
    fetchSessionFeedbackSuccess,
    fetchSessionFeedbackFailure,
    generateSessionFeedback,
    generateSessionFeedbackSuccess,
    generateSessionFeedbackFailure,
    fetchSessionAnalytics,
    fetchSessionAnalyticsSuccess,
    fetchSessionAnalyticsFailure,
} from '@/store/slices/conversation/conversationProgressSlice';

import {
    createAIChatSession,
    sendMessageWithAI,
    clearError as clearErrorSlice,
    createAIChatSessionSuccess,
    createAIChatSessionFailure,
    sendMessageWithAISuccess,
    sendMessageWithAIFailure,
    fetchAIConversationSessionsFailure,
    fetchAIConversationSessionsSuccess,
    fetchAIConversationSessions,
    fetchAIConversationSession,
    fetchAIConversationSessionSuccess,
    fetchAIConversationSessionFailure,
    createAnonymousAIChatSession,
    createAnonymousAIChatSessionSuccess,
    createAnonymousAIChatSessionFailure,
    sendAnonymousMessageWithAI,
    sendAnonymousMessageWithAISuccess,
    sendAnonymousMessageWithAIFailure,
} from '@/store/slices/conversation/aiConversationSessionSlice';

// Worker Sagas for Session Management
function* createConversationSessionSaga(action: PayloadAction<CreateConversationRequest>) {
    try {
        const response: ConversationSession = yield call(conversationService.createConversationSession, action.payload);
        yield put(createConversationSessionSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to create conversation session';
        yield put(createConversationSessionFailure(errorMessage));
    }
}

function* fetchConversationSessionSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationSession = yield call(conversationService.getSession, action.payload.sessionId);
        yield put(fetchConversationSessionSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch conversation session';
        yield put(fetchConversationSessionFailure(errorMessage));
    }
}

function* updateConversationSessionSaga(
    action: PayloadAction<{ sessionId: string; data: Partial<ConversationSession> }>,
) {
    try {
        const { sessionId, data } = action.payload;
        const response: ConversationSession = yield call(
            conversationService.updateConversationSession,
            sessionId,
            data,
        );
        yield put(updateConversationSessionSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to update conversation session';
        yield put(updateConversationSessionFailure(errorMessage));
    }
}

function* completeConversationSessionSaga(action: PayloadAction<{ sessionId: string, sessionData: ConversationSession }>) {
    try {
        const response: SessionCompletionData = yield call(
            conversationService.completeConversationSession,
            action.payload.sessionId,
            action.payload.sessionData
        );
        yield put(completeConversationSessionSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to complete conversation session';
        yield put(completeConversationSessionFailure(errorMessage));
    }
}

function* fetchSessionLessonsSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: LessonContent[] = yield call(conversationService.getSessionLessons, action.payload.sessionId);
        yield put(fetchSessionLessonsSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch session lessons';
        yield put(fetchSessionLessonsFailure(errorMessage));
    }
}

function* startLessonSaga(action: PayloadAction<{ lessonId: string }>) {
    try {
        const response: LessonContent = yield call(conversationService.startLesson, action.payload.lessonId);
        yield put(startLessonSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to start lesson';
        yield put(startLessonFailure(errorMessage));
    }
}

function* completeLessonSaga(
    action: PayloadAction<{
        lessonId: string;
        completionData: { completion_score?: number; time_spent?: string; lesson_data?: Record<string, any> };
    }>,
) {
    try {
        const { lessonId, completionData } = action.payload;
        const response: LessonContent = yield call(conversationService.completeLesson, lessonId, completionData);
        yield put(completeLessonSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to complete lesson';
        yield put(completeLessonFailure(errorMessage));
    }
}

function* fetchUserSessionsSaga() {
    try {
        const response: ConversationSession[] = yield call(conversationService.getConversationSessions);

        // If we have a session, use it
        if (response.length > 0) {
            const session = response[0];
            yield put(fetchUserSessionsSuccess(response));
            // Also set it as the current session if none is set
            yield put(setCurrentSession(session));

            // Automatically load session data (messages, lessons, activities)
            if (session.id) {
                try {
                    // Load messages for the session
                    yield put(fetchSessionMessages({ sessionId: session.id }));

                    // Load lessons for the session
                    yield put(fetchSessionLessons({ sessionId: session.id }));

                    // Load session progress
                    yield put(fetchSessionProgress({ sessionId: session.id }));

                    // If session has a daily_schedule, load enhanced session data with activities
                    if (session.daily_schedule?.id || session.daily_schedule_id) {
                        try {
                            const sessionWithLessons: ConversationSession = yield call(
                                conversationService.getSessionWithLessons,
                                session.id,
                            );
                        } catch (lessonError) {
                            console.warn('Failed to load enhanced session data:', lessonError);
                        }
                    }
                } catch (dataError) {
                    console.warn('Failed to load session data:', dataError);
                }
            }
        } else {
            // No session found, return empty array
            yield put(fetchUserSessionsSuccess([]));
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch user sessions';
        yield put(fetchUserSessionsFailure(errorMessage));
    }
}

// Worker Sagas for Message Management
function* fetchSessionMessagesSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationMessage[] = yield call(
            conversationService.getSessionMessages,
            action.payload.sessionId,
        );
        yield put(fetchSessionMessagesSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch session messages';
        yield put(fetchSessionMessagesFailure(errorMessage));
    }
}

function* sendMessageSaga(action: PayloadAction<SubmitMessageRequest>) {
    try {
        const response: { user_message: ConversationMessage; ai_response: ConversationMessage } = yield call(
            conversationService.submitMessage,
            action.payload,
        );
        yield put(sendMessageSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send message';
        yield put(sendMessageFailure(errorMessage));
    }
}

function* pinMessageSaga(action: PayloadAction<{ messageId: string; isPinned?: boolean }>) {
    try {
        const { messageId, isPinned = true } = action.payload;
        const response: ConversationMessage = yield call(conversationService.pinMessage, messageId, isPinned);
        yield put(pinMessageSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to pin message';
        yield put(pinMessageFailure(errorMessage));
    }
}

function* fetchPinnedMessagesSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationMessage[] = yield call(
            conversationService.getPinnedMessages,
            action.payload.sessionId,
        );
        yield put(fetchPinnedMessagesSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch pinned messages';
        yield put(fetchPinnedMessagesFailure(errorMessage));
    }
}

// Worker Sagas for Progress Management
function* fetchSessionProgressSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationProgress = yield call(
            conversationService.getSessionProgress,
            action.payload.sessionId,
        );
        yield put(fetchSessionProgressSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch session progress';
        yield put(fetchSessionProgressFailure(errorMessage));
    }
}

function* updateSessionProgressSaga(
    action: PayloadAction<{ sessionId: string; progressData: Partial<ConversationProgress> }>,
) {
    try {
        const { sessionId, progressData } = action.payload;
        const response: ConversationProgress = yield call(
            conversationService.updateSessionProgress,
            sessionId,
            progressData,
        );
        yield put(updateSessionProgressSuccess(response));
    } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update session progress';
        yield put(updateSessionProgressFailure(errorMessage));
    }
}

function* fetchSessionFeedbackSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationFeedback | null = yield call(
            conversationService.getSessionFeedback,
            action.payload.sessionId,
        );
        if (response) {
            yield put(fetchSessionFeedbackSuccess(response));
        } else {
            yield put(fetchSessionFeedbackFailure('No feedback available for this session'));
        }
    } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch session feedback';
        yield put(fetchSessionFeedbackFailure(errorMessage));
    }
}

function* generateSessionFeedbackSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationFeedback = yield call(
            conversationService.generateSessionFeedback,
            action.payload.sessionId,
        );
        yield put(generateSessionFeedbackSuccess(response));
    } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate session feedback';
        yield put(generateSessionFeedbackFailure(errorMessage));
    }
}

function* fetchSessionAnalyticsSaga(action: PayloadAction<{ sessionId: string }>) {
    try {
        const response: ConversationAnalytics = yield call(
            conversationService.getSessionAnalytics,
            action.payload.sessionId,
        );
        yield put(fetchSessionAnalyticsSuccess(response));
    } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch session analytics';
        yield put(fetchSessionAnalyticsFailure(errorMessage));
    }
}

// Watcher Sagas for Session Management
function* watchCreateConversationSession() {
    yield takeLatest(createConversationSession.type, createConversationSessionSaga);
}

function* watchFetchConversationSession() {
    yield takeLatest(fetchConversationSession.type, fetchConversationSessionSaga);
}

function* watchUpdateConversationSession() {
    yield takeEvery(updateConversationSession.type, updateConversationSessionSaga);
}

function* watchCompleteConversationSession() {
    yield takeLatest(completeConversationSession.type, completeConversationSessionSaga);
}

function* watchFetchSessionLessons() {
    yield takeLatest(fetchSessionLessons.type, fetchSessionLessonsSaga);
}

function* watchStartLesson() {
    yield takeEvery(startLesson.type, startLessonSaga);
}

function* watchCompleteLesson() {
    yield takeEvery(completeLesson.type, completeLessonSaga);
}

function* watchFetchUserSessions() {
    yield takeLatest(fetchUserSessions.type, fetchUserSessionsSaga);
}

// Watcher Sagas for Message Management
function* watchFetchSessionMessages() {
    yield takeLatest(fetchSessionMessages.type, fetchSessionMessagesSaga);
}

function* watchSendMessage() {
    yield takeEvery(sendMessage.type, sendMessageSaga);
}

function* watchPinMessage() {
    yield takeEvery(pinMessage.type, pinMessageSaga);
}

function* watchFetchPinnedMessages() {
    yield takeLatest(fetchPinnedMessages.type, fetchPinnedMessagesSaga);
}

// Watcher Sagas for Progress Management
function* watchFetchSessionProgress() {
    yield takeLatest(fetchSessionProgress.type, fetchSessionProgressSaga);
}

function* watchUpdateSessionProgress() {
    yield takeEvery(updateSessionProgress.type, updateSessionProgressSaga);
}

function* watchFetchSessionFeedback() {
    yield takeLatest(fetchSessionFeedback.type, fetchSessionFeedbackSaga);
}

function* watchGenerateSessionFeedback() {
    yield takeLatest(generateSessionFeedback.type, generateSessionFeedbackSaga);
}

function* watchFetchSessionAnalytics() {
    yield takeLatest(fetchSessionAnalytics.type, fetchSessionAnalyticsSaga);
}

function* createAIConversationSessionSaga(action: PayloadAction<CreateAIConversationRequest>) {
    try {
        const response: { id: string; messages: ConversationMessage[] } = yield call(
            conversationService.createAIConversationSession,
            action.payload,
        );
        yield put(createAIChatSessionSuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create chat session';
        yield put(createAIChatSessionFailure(errorMessage));
    }
}

function* createAnonymousAIConversationSessionSaga(action: PayloadAction<CreateAIConversationRequest>) {
    try {
        const response: { id: string; messages: ConversationMessage[] } = yield call(
            conversationService.createAnonymousAIConversationSession,
            action.payload,
        );
        yield put(createAnonymousAIChatSessionSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to create anonymous chat session';
        yield put(createAnonymousAIChatSessionFailure(errorMessage));
    }
}

function* sendMessageWithAISaga(action: PayloadAction<SubmitMessageRequest>) {
    try {
        const response: { user_message: ConversationMessage; ai_response: ConversationMessage } = yield call(
            conversationService.sendMessage,
            action.payload,
        );
        yield put(sendMessageWithAISuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send message';
        yield put(sendMessageWithAIFailure(errorMessage));
    }
}

function* sendAnonymousMessageWithAISaga(action: PayloadAction<SubmitMessageRequest>) {
    try {
        const response: { user_message: ConversationMessage; ai_response: ConversationMessage } = yield call(
            conversationService.sendAnonymousMessage,
            action.payload,
        );
        yield put(sendAnonymousMessageWithAISuccess(response));
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send anonymous message';
        yield put(sendAnonymousMessageWithAIFailure(errorMessage));
    }
}

function* fetchAIConversationSessionsSaga(action: PayloadAction<AIConversationFilters | undefined>) {
    try {
        const response: AIConversationResponse = yield call(conversationService.getChatSessions, action.payload);
        yield put(fetchAIConversationSessionsSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to fetch AI conversation sessions';
        yield put(fetchAIConversationSessionsFailure(errorMessage));
    }
}

function* fetchAIConversationSessionSaga(action: PayloadAction<string>) {
    try {
        const response: AIConversationSession = yield call(conversationService.getChatDetailsSession, action.payload);
        yield put(fetchAIConversationSessionSuccess(response));
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to fetch AI conversation session';
        yield put(fetchAIConversationSessionFailure(errorMessage));
    }
}

// Root Conversation Saga
export default function* conversationSaga() {
    yield all([
        // Session Management
        fork(watchCreateConversationSession),
        fork(watchFetchConversationSession),
        fork(watchUpdateConversationSession),
        fork(watchCompleteConversationSession),
        fork(watchFetchSessionLessons),
        fork(watchStartLesson),
        fork(watchCompleteLesson),
        fork(watchFetchUserSessions),

        // Message Management
        fork(watchFetchSessionMessages),
        fork(watchSendMessage),
        fork(watchPinMessage),
        fork(watchFetchPinnedMessages),

        // Progress Management
        fork(watchFetchSessionProgress),
        fork(watchUpdateSessionProgress),
        fork(watchFetchSessionFeedback),
        fork(watchGenerateSessionFeedback),
        fork(watchFetchSessionAnalytics),

        // AI Conversation
        yield takeLatest(createAIChatSession.type, createAIConversationSessionSaga),
        yield takeLatest(createAnonymousAIChatSession.type, createAnonymousAIConversationSessionSaga),
        yield takeLatest(sendMessageWithAI.type, sendMessageWithAISaga),
        yield takeLatest(sendAnonymousMessageWithAI.type, sendAnonymousMessageWithAISaga),
        yield takeLatest(fetchAIConversationSessions.type, fetchAIConversationSessionsSaga),
        yield takeLatest(fetchAIConversationSession.type, fetchAIConversationSessionSaga),
    ]);
}
